<script setup>
import { computed } from 'vue';

import { frontendURL } from 'dashboard/helper/URLHelper.js';
import { dynamicTime } from 'shared/helpers/timeHelper';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  id: {
    type: Number,
    default: 0,
  },
  inbox: {
    type: Object,
    default: () => ({}),
  },
  name: {
    type: String,
    default: '',
  },
  email: {
    type: String,
    default: '',
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
  createdAt: {
    type: [String, Date, Number],
    default: '',
  },
  messageId: {
    type: Number,
    default: 0,
  },
  emailSubject: {
    type: String,
    default: '',
  },
});

const navigateTo = computed(() => {
  const params = {};
  if (props.messageId) {
    params.messageId = props.messageId;
  }
  return frontendURL(
    `accounts/${props.accountId}/conversations/${props.id}`,
    params
  );
});

const createdAtTime = computed(() =>
  props.createdAt ? dynamicTime(props.createdAt) : ''
);

const infoItems = computed(() => [
  {
    label: 'SEARCH.FROM',
    value: props.name,
    show: !!props.name,
  },
  {
    label: 'SEARCH.EMAIL',
    value: props.email,
    show: !!props.email,
  },
  {
    label: 'SEARCH.EMAIL_SUBJECT',
    value: props.emailSubject,
    show: !!props.emailSubject,
  },
]);

const visibleInfoItems = computed(() =>
  infoItems.value.filter(item => item.show)
);

const hasInbox = computed(() => Boolean(props.inbox?.id || props.inbox?.name));
</script>

<template>
  <router-link :to="navigateTo" class="result-item">
    <Avatar
      name="chats"
      :size="32"
      icon-name="i-lucide-messages-square"
      class="result-item__avatar"
    />
    <div class="result-item__body">
      <div class="result-item__meta">
        <div class="result-item__tags">
          <span class="result-item__id">#{{ id }}</span>
          <div v-if="hasInbox" class="result-item__inbox">
            <InboxName :inbox="inbox" />
          </div>
        </div>
        <span v-if="createdAtTime" class="result-item__time">
          {{ createdAtTime }}
        </span>
      </div>
      <div v-if="visibleInfoItems.length" class="result-item__info">
        <h5 v-for="item in visibleInfoItems" :key="item.label">
          <span>{{ $t(item.label) }}:</span>
          {{ item.value }}
        </h5>
      </div>
      <slot />
    </div>
  </router-link>
</template>

<style scoped>
.result-item {
  display: flex;
  gap: 10px;
  padding: 12px;
  border: 1px solid transparent;
  border-radius: 12px;
  color: inherit;
  text-decoration: none;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.result-item:hover {
  border-color: var(--wlab-border-light, var(--n-weak));
  background: color-mix(in srgb, var(--wlab-accent, #6366f1) 6%, transparent);
}

.result-item__avatar :deep(span) {
  border-radius: 10px;
}

.result-item__body {
  flex: 1;
  min-width: 0;
}

.result-item__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.result-item__tags {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.result-item__id {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid var(--wlab-border-light, var(--n-weak));
  background: color-mix(
    in srgb,
    var(--wlab-text-primary, #fff) 5%,
    transparent
  );
  color: var(--wlab-text-secondary, var(--n-slate-11));
  font-size: 12px;
  font-weight: 600;
}

.result-item__inbox {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 22px;
  padding: 0 8px;
  border-radius: 8px;
  background: color-mix(
    in srgb,
    var(--wlab-text-primary, #fff) 5%,
    transparent
  );
}

.result-item__time {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--wlab-text-muted, var(--n-slate-11));
}

.result-item__info {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
}

.result-item__info h5 {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-primary, var(--n-slate-12));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-item__info span {
  font-size: 12px;
  font-weight: 400;
  color: var(--wlab-text-muted, var(--n-slate-11));
}
</style>
